import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { classroomAPI, teacherAPI } from '../utils/api';
import type { Classroom } from '../types/classroom';
import type { Teacher, TeacherCreateRequest } from '../types/teacher';

const emptyForm: TeacherCreateRequest = { name: '', email: '', phone: '', status: 'active', classroom_ids: [] };

const buildTeacherFormData = (payload: TeacherCreateRequest, profilePhoto: File) => {
  const data = new FormData();
  data.append('name', payload.name);
  data.append('email', payload.email);
  if (payload.phone) data.append('phone', payload.phone);
  if (payload.status) data.append('status', String(payload.status));
  (payload.classroom_ids || []).forEach((classroomId) => {
    data.append('classroom_ids[]', String(classroomId));
  });
  data.append('profile_photo', profilePhoto);
  return data;
};

const getSavedTeacherId = (response: { data?: Teacher; teacher?: Teacher }) => response.teacher?.id || response.data?.id;

const Teachers: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'teachers' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const { canManageTeachers } = useRolePermissions();
  const nurseryId = currentNursery?.nursery_id || '';
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [form, setForm] = useState<TeacherCreateRequest>(emptyForm);
  const [editing, setEditing] = useState<Teacher | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [portalUsername, setPortalUsername] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);

  const selectedClassroomIds = form.classroom_ids || [];

  const extractApiError = (err: any, fallback: string) => {
    if (err?.response?.data?.errors) {
      const errors = err.response.data.errors;
      return Object.keys(errors).map((key) => {
        const value = errors[key];
        return `${key}: ${Array.isArray(value) ? value.join(', ') : value}`;
      }).join(' | ');
    }
    return err?.response?.data?.message || err?.message || fallback;
  };

  const loadData = useCallback(async () => {
    if (!nurseryId) return;
    setLoading(true);
    setError(null);
    try {
      const [teacherResponse, classroomResponse] = await Promise.all([
        teacherAPI.list(nurseryId),
        classroomAPI.getClassrooms(nurseryId),
      ]);
      setTeachers(teacherResponse.teachers || teacherResponse.data || []);
      setClassrooms(classroomResponse.classrooms || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [nurseryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalError(null);
    setModalSuccess(null);
    setPortalUsername('');
    setPortalPassword('');
    setProfilePhoto(null);
    setShowModal(true);
  };

  const openEdit = (teacher: Teacher) => {
    setEditing(teacher);
    setForm({
      name: teacher.name,
      email: teacher.email,
      phone: teacher.phone || '',
      status: teacher.status === 'inactive' ? 'inactive' : 'active',
      classroom_ids: teacher.classroom_ids || teacher.classrooms?.map((c) => c.id) || [],
    });
    setModalError(null);
    setModalSuccess(null);
    setPortalUsername(teacher.username || '');
    setPortalPassword('');
    setProfilePhoto(null);
    setShowModal(true);
  };

  const toggleClassroom = (classroomId: number) => {
    const selected = new Set(selectedClassroomIds);
    if (selected.has(classroomId)) {
      selected.delete(classroomId);
    } else {
      selected.add(classroomId);
    }
    setForm({ ...form, classroom_ids: Array.from(selected) });
  };

  const handleGrantPortalAccess = async () => {
    if (!nurseryId || !editing || !portalUsername || !portalPassword) return;
    setPortalLoading(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      await teacherAPI.grantPortalAccess(nurseryId, editing.id, { username: portalUsername, password: portalPassword });
      setEditing({ ...editing, username: portalUsername, has_portal_access: true, must_change_password: true });
      setTeachers((items) => items.map((teacher) => (
        teacher.id === editing.id
          ? { ...teacher, username: portalUsername, has_portal_access: true, must_change_password: true }
          : teacher
      )));
      setPortalPassword('');
      setModalSuccess(t('portalAccessGranted'));
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('grantAccessFailed')));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleResetPortalPassword = async () => {
    if (!nurseryId || !editing || !portalPassword) return;
    setPortalLoading(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      await teacherAPI.resetPortalPassword(nurseryId, editing.id, { password: portalPassword });
      setEditing({ ...editing, must_change_password: true });
      setTeachers((items) => items.map((teacher) => (
        teacher.id === editing.id ? { ...teacher, must_change_password: true } : teacher
      )));
      setPortalPassword('');
      setModalSuccess(t('portalPasswordReset'));
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('resetPasswordFailed')));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRevokePortalAccess = async () => {
    if (!nurseryId || !editing) return;
    setPortalLoading(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      await teacherAPI.revokePortalAccess(nurseryId, editing.id);
      setEditing({ ...editing, username: null, has_portal_access: false, must_change_password: false });
      setTeachers((items) => items.map((teacher) => (
        teacher.id === editing.id
          ? { ...teacher, username: null, has_portal_access: false, must_change_password: false }
          : teacher
      )));
      setShowRevokeConfirm(false);
      setPortalPassword('');
      setModalSuccess(t('portalAccessRevoked'));
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('revokeAccessFailed')));
    } finally {
      setPortalLoading(false);
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!nurseryId) return;
    setSaving(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      const teacherPayload: TeacherCreateRequest = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        status: form.status === 'inactive' ? 'inactive' : 'active',
        classroom_ids: form.classroom_ids || [],
      };
      const payload = profilePhoto ? buildTeacherFormData(teacherPayload, profilePhoto) : teacherPayload;

      if (editing) {
        await teacherAPI.update(nurseryId, editing.id, payload);
        await teacherAPI.assignClassrooms(nurseryId, editing.id, teacherPayload.classroom_ids || []);
      } else {
        const response = await teacherAPI.create(nurseryId, payload);
        const teacherId = getSavedTeacherId(response);
        if (teacherId) {
          await teacherAPI.assignClassrooms(nurseryId, teacherId, teacherPayload.classroom_ids || []);
        }
      }
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (!canManageTeachers) {
    return <Layout><Container fluid className="py-4"><Alert variant="danger">{t('noAccess')}</Alert></Container></Layout>;
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div><h2 className="fw-bold text-primary mb-1">{t('title')}</h2><div className="text-muted">{currentNursery?.name || nurseryId}</div></div>
          <Button onClick={openCreate} disabled={!nurseryId}>{t('addTeacher')}</Button>
        </div>
        {!nurseryId && <Alert variant="warning">{t('selectNursery')}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light"><tr><th>{tc('labels.name')}</th><th>{tc('labels.email')}</th><th>{tc('labels.phone')}</th><th>{t('classrooms')}</th><th>{t('portal')}</th><th>{tc('labels.status')}</th><th className="text-end">{tc('labels.actions')}</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-5"><Spinner animation="border" /></td></tr> : teachers.map((teacher) => (
                  <tr key={teacher.id}>
                    <td>{teacher.name}</td><td>{teacher.email}</td><td>{teacher.phone || '-'}</td>
                    <td>{teacher.classrooms?.map((c) => c.name).join(', ') || teacher.classroom_ids?.join(', ') || '-'}</td>
                    <td>
                      <Badge bg={teacher.has_portal_access ? 'success' : 'secondary'}>
                        {teacher.has_portal_access ? t('granted') : t('noPortalAccess')}
                      </Badge>
                    </td>
                    <td><Badge bg={teacher.status === 'inactive' ? 'secondary' : 'success'}>{teacher.status === 'inactive' ? tc('status.inactive') : tc('status.active')}</Badge></td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(teacher)}>{tc('buttons.edit')}</Button>
                      {teacher.status !== 'inactive' && (
                        <Button size="sm" variant="outline-danger" onClick={async () => { await teacherAPI.deactivate(nurseryId, teacher.id); await loadData(); }}>{t('deactivate')}</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && teachers.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-5">{t('noTeachers')}</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
          <Form onSubmit={save}>
            <Modal.Header closeButton><Modal.Title>{editing ? t('editTeacher') : t('addTeacher')}</Modal.Title></Modal.Header>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              {modalSuccess && <Alert variant="success">{modalSuccess}</Alert>}
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{tc('labels.name')}</Form.Label><Form.Control required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{tc('labels.email')}</Form.Label><Form.Control required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{tc('labels.phone')}</Form.Label><Form.Control value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Form.Group></Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{tc('labels.status')}</Form.Label>
                    <Form.Select value={String(form.status || 'active')} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option value="active">{tc('status.active')}</option>
                      <option value="inactive">{tc('status.inactive')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('profilePhoto')}</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => setProfilePhoto((e.currentTarget as HTMLInputElement).files?.[0] || null)}
                    />
                    {profilePhoto && <Form.Text className="text-muted">{profilePhoto.name}</Form.Text>}
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">{t('assignedClassrooms')}</Form.Label>
                      <span className="text-muted small">{t('selectedCount', { count: selectedClassroomIds.length })}</span>
                    </div>
                    <div className="border rounded p-3 bg-light">
                      {selectedClassroomIds.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {classrooms.filter((classroom) => selectedClassroomIds.includes(classroom.id)).map((classroom) => (
                            <Badge key={classroom.id} bg="primary" className="px-2 py-2">
                              {classroom.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <Row className="g-2">
                        {classrooms.map((classroom) => (
                          <Col md={6} key={classroom.id}>
                            <Form.Check
                              type="checkbox"
                              id={`teacher-classroom-${classroom.id}`}
                              className="bg-white border rounded px-3 py-2"
                              label={classroom.name}
                              checked={selectedClassroomIds.includes(classroom.id)}
                              onChange={() => toggleClassroom(classroom.id)}
                            />
                          </Col>
                        ))}
                        {classrooms.length === 0 && <Col><div className="text-muted small">{t('noClassrooms')}</div></Col>}
                      </Row>
                    </div>
                  </Form.Group>
                </Col>
                {editing && (
                  <Col md={12} className="mb-3 mt-2">
                    <div className="border rounded p-3 bg-light">
                      <h6 className="border-bottom pb-2 mb-3">{t('portalAccess')}</h6>
                      <div className="mb-3">
                        <span className="small text-muted me-2">{t('teacherPortalUrl')}:</span>
                        <a href={`${window.location.origin}/teacher/login`} target="_blank" rel="noopener noreferrer" className="small">
                          {`${window.location.origin}/teacher/login`}
                        </a>
                      </div>
                      {!editing.has_portal_access ? (
                        <>
                          <p className="small text-muted mb-3">{t('grantAccessHint')}</p>
                          <Row>
                            <Col md={5}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small">{t('portalUsername')}</Form.Label>
                                <Form.Control type="text" size="sm" value={portalUsername} onChange={(e) => setPortalUsername(e.target.value)} placeholder={t('usernamePlaceholder')} />
                              </Form.Group>
                            </Col>
                            <Col md={5}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small">{t('temporaryPassword')}</Form.Label>
                                <Form.Control type="password" size="sm" value={portalPassword} onChange={(e) => setPortalPassword(e.target.value)} placeholder={t('passwordPlaceholder')} />
                              </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                              <Button type="button" variant="outline-primary" size="sm" className="w-100 mb-2" onClick={handleGrantPortalAccess} disabled={portalLoading || !portalUsername || !portalPassword}>
                                {portalLoading ? <Spinner animation="border" size="sm" /> : t('grant')}
                              </Button>
                            </Col>
                          </Row>
                        </>
                      ) : (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <span className="small text-muted me-2">{t('portalUsername')}:</span>
                              <Badge bg="success" className="me-2">{editing.username || portalUsername || editing.email}</Badge>
                              {editing.must_change_password && <Badge bg="warning" text="dark">{t('mustChangePassword')}</Badge>}
                            </div>
                            <Button type="button" variant="outline-danger" size="sm" onClick={() => setShowRevokeConfirm(true)} disabled={portalLoading}>{t('revokeAccess')}</Button>
                          </div>
                          <div className="border-top pt-3">
                            <h6>{t('resetPassword')}</h6>
                            <Row>
                              <Col md={9}>
                                <Form.Control type="password" size="sm" value={portalPassword} onChange={(e) => setPortalPassword(e.target.value)} placeholder={t('newTemporaryPassword')} />
                              </Col>
                              <Col md={3}>
                                <Button type="button" variant="outline-warning" size="sm" className="w-100" onClick={handleResetPortalPassword} disabled={portalLoading || !portalPassword}>
                                  {portalLoading ? <Spinner animation="border" size="sm" /> : t('resetPassword')}
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>{tc('buttons.cancel')}</Button><Button type="submit" disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : tc('buttons.save')}</Button></Modal.Footer>
          </Form>
        </Modal>
        <Modal show={showRevokeConfirm} onHide={() => setShowRevokeConfirm(false)} centered>
          <Modal.Header closeButton><Modal.Title>{t('revokeAccess')}</Modal.Title></Modal.Header>
          <Modal.Body>{t('confirmRevokeAccess')}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRevokeConfirm(false)}>{tc('buttons.cancel')}</Button>
            <Button variant="danger" onClick={handleRevokePortalAccess} disabled={portalLoading}>
              {portalLoading ? <Spinner animation="border" size="sm" /> : t('revokeAccess')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Teachers;
